import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Flex,
    Text,
    Button,
    Dropdown,
    Alert,
    ExclamationTriangleIcon,
    Loader,
    Checkbox,
    ShiftActivityIcon,
} from '@fluentui/react-northstar';
import { app, dialog } from '@microsoft/teams-js';
import { getMeetingData } from '../../api/publicapp-api';
import { getBookingsById, getStaffMembers, updateStaffMember } from '../../api/bookings-api';
import { getUserSettings } from '../../api/user-api';
import * as moment from 'moment';
import 'moment/locale/pt-br';
import './bookings-page.scss';

interface BookingsAvailabilityProps { }

interface RouteParams {
    ancora?: string;
}

const BookingsAvailability: React.FC<BookingsAvailabilityProps> = () => {
    const { t } = useTranslation();
    const { ancora } = useParams<RouteParams>();

    moment.locale('pt-br');

    const [loading, setLoading] = useState<boolean>(true);
    const [bookingId, setBookingId] = useState<string>();
    const [userLoginHint, setUserLoginHint] = useState<string>();
    const [staffMembers, setStaffMembers] = useState<any[]>([]);
    const [selectedStaffMember, setSelectedStaffMember] = useState<any>();
    const [staffMemberId, setStaffMemberId] = useState<string>();
    const [useBusinessHours, setUseBusinessHours] = useState<boolean>(false);
    const [businessHours, setBusinessHours] = useState<Record<string, { start: string; end: string }> | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [updatedStaffMember, setUpdatedStaffMember] = useState(false);
    const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({});
    const [previousAvailability, setPreviousAvailability] = useState<Record<string, { start?: string; end?: string }>>({});
    const [error, setError] = useState<string>();

    const staffMap = useMemo(() => {
        const map = new Map<string, any>();
        for (const member of staffMembers ?? []) {
            const email = member.emailAddress?.trim().toLowerCase();
            if (email) {
                map.set(email, member);
            }
        }
        return map;
    }, [staffMembers]);

    const daysOfWeek = [
        { key: 'monday', label: 'Segunda-feira' },
        { key: 'tuesday', label: 'Terça-feira' },
        { key: 'wednesday', label: 'Quarta-feira' },
        { key: 'thursday', label: 'Quinta-feira' },
        { key: 'friday', label: 'Sexta-feira' },
    ];

    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = moment({ hour, minute }).format('HH:mm');
                times.push({ key: time, header: time });
            }
        }
        return times;
    };

    const timeOptions = useMemo(generateTimeOptions, []);
    const [availability, setAvailability] = useState<Record<string, { start?: string; end?: string }>>({});
    const [initialAvailability, setInitialAvailability] = useState<Record<string, { start?: string; end?: string }>>({});

    const isAvailabilityChanged = () => {
        return JSON.stringify(availability) !== JSON.stringify(initialAvailability);
    };

    const handleTimeChange = (dayKey: string, field: 'start' | 'end', value: string) => {
        setAvailability(prev => ({
            ...prev,
            [dayKey]: {
                ...prev[dayKey],
                [field]: value,
            },
        }));
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const result = await getUserSettings(ancora);
                if (result.data) {
                    setUserLoginHint(result.data.email);
                }
            } catch (error) {
                console.error('Erro ao carregar usuário:', error);
                setError("Erro ao carregar usuário.");
            }
        };

        const fetchMeetingData = async () => {
            try {
                const result = await getMeetingData(ancora);
                if (result.data) {
                    setBookingId(result.data.bookingsId);

                    await Promise.all([
                        fetchStaffMembers(result.data.bookingsId),
                        fetchBookings(result.data.bookingsId),
                    ]).then(() => setLoading(false));
                }
            } catch (error) {
                console.error('Erro ao carregar meeting data:', error);
                setError("Erro ao obter informações da unidade.");
            }
        };

        const fetchStaffMembers = async (bookingId: string) => {
            try {
                const result = await getStaffMembers(bookingId);
                if (result.data) {
                    setStaffMembers(result.data);
                } else {
                    setError("Não foram encontrados atendentes para este serviço do Bookings.");
                }
            } catch (error) {
                console.error('Erro ao carregar staff members:', error);
                setError("Erro ao obter informações de atendentes.");
            }
        };

        const fetchBookings = async (bookingId: string) => {
            try {
                const result = await getBookingsById(bookingId);
                if (result.data) {
                    console.log(result.data);

                    if (result.data?.businessHours) {
                        const bh = result.data.businessHours.reduce((acc: any, day: any) => {
                            const timeSlot = day.timeSlots?.[0];
                            if (timeSlot) {
                                const dayKeyMap = {
                                    0: 'sunday',
                                    1: 'monday',
                                    2: 'tuesday',
                                    3: 'wednesday',
                                    4: 'thursday',
                                    5: 'friday',
                                    6: 'saturday',
                                };

                                const key = dayKeyMap[day.day];
                                acc[key] = {
                                    start: moment({ hour: timeSlot.startTime.hour, minute: timeSlot.startTime.minute }).format('HH:mm'),
                                    end: moment({ hour: timeSlot.endTime.hour, minute: timeSlot.endTime.minute }).format('HH:mm'),
                                };
                            }
                            return acc;
                        }, {});

                        setBusinessHours(bh);
                    }
                } else {
                    setError("Erro ao obter configurações do bookings.");
                }
            } catch (error) {
                console.error('Erro ao obter configurações do bookings:', error);
                setError("Erro ao obter configurações do bookings.");
            }
        };

        app.initialize();
        app.getContext().then(async () => {
            await fetchUserData();
            await fetchMeetingData();
        });
    }, []);

    useEffect(() => {
        if (!userLoginHint || staffMap.size === 0) return;
        const normalizedEmail = userLoginHint.trim().toLowerCase();
        const matchedStaff = staffMap.get(normalizedEmail);
        if (matchedStaff) {
            setSelectedStaffMember(matchedStaff);
            setStaffMemberId(matchedStaff.id);
            setUseBusinessHours(matchedStaff.useBusinessHours);
        }
    }, [userLoginHint, staffMap]);

    useEffect(() => {
        if (useBusinessHours && businessHours) {
            setAvailability(businessHours);
            setInitialAvailability(businessHours);
        }
    }, [useBusinessHours, businessHours]);

    useEffect(() => {
        if (!selectedStaffMember || useBusinessHours || !selectedStaffMember.workingHours) return;

        const staffHours = selectedStaffMember.workingHours.reduce((acc: any, day: any) => {
            const timeSlot = day.timeSlots?.[0];
            const dayKeyMap = {
                0: 'sunday',
                1: 'monday',
                2: 'tuesday',
                3: 'wednesday',
                4: 'thursday',
                5: 'friday',
                6: 'saturday',
            };

            const key = dayKeyMap[day.day];

            if (timeSlot) {
                acc[key] = {
                    start: moment({ hour: timeSlot.startTime.hour, minute: timeSlot.startTime.minute }).format('HH:mm'),
                    end: moment({ hour: timeSlot.endTime.hour, minute: timeSlot.endTime.minute }).format('HH:mm'),
                };
            }
            return acc;
        }, {});

        const { hours, enabled } = selectedStaffMember.workingHours.reduce((acc: any, day: any) => {
            const timeSlot = day.timeSlots?.[0];
            const dayKeyMap = {
                0: 'sunday',
                1: 'monday',
                2: 'tuesday',
                3: 'wednesday',
                4: 'thursday',
                5: 'friday',
                6: 'saturday',
            };

            const key = dayKeyMap[day.day];

            acc.enabled[key] = !!timeSlot;

            if (timeSlot) {
                acc.hours[key] = {
                    start: moment({ hour: timeSlot.startTime.hour, minute: timeSlot.startTime.minute }).format('HH:mm'),
                    end: moment({ hour: timeSlot.endTime.hour, minute: timeSlot.endTime.minute }).format('HH:mm'),
                };
            }

            return acc;
        }, { hours: {}, enabled: {} });

        setAvailability(staffHours);
        setInitialAvailability(staffHours);
        setEnabledDays(enabled);
    }, [selectedStaffMember, useBusinessHours]);

    return (
        <div className="bookings-availability">
            {error &&
                <Alert danger content={error} icon={<ExclamationTriangleIcon outline />} />
            }

            {loading ? (
                <Loader label="Carregando..." />
            ) : (
                <Flex column gap="gap.large">
                    <Checkbox
                        label="Utilizar o horário comercial"
                        checked={useBusinessHours}
                        toggle
                        onChange={(e, { checked }) => setUseBusinessHours(!!checked)}
                    />

                    <Flex className="availability-options" column gap="gap.medium">
                        {daysOfWeek.map(day => (
                            <Flex key={day.key} gap="gap.medium" vAlign="center">
                                <Text styles={{ minWidth: "12rem" }} content={day.label} weight="semibold" />

                                <Dropdown
                                    placeholder="Início"
                                    items={timeOptions}
                                    value={timeOptions.find(item => item.key === availability[day.key]?.start)}
                                    onChange={(e, { value }) => handleTimeChange(day.key, 'start', (value as any).key)}
                                    checkable
                                    fluid
                                    disabled={useBusinessHours}
                                />

                                <Dropdown
                                    placeholder="Fim"
                                    items={timeOptions}
                                    value={timeOptions.find(item => item.key === availability[day.key]?.end)}
                                    onChange={(e, { value }) => handleTimeChange(day.key, 'end', (value as any).key)}
                                    checkable
                                    fluid
                                    disabled={useBusinessHours}
                                />

                                {!useBusinessHours && (
                                    <Checkbox
                                        toggle
                                        checked={enabledDays[day.key] ?? true}
                                        onChange={(e, { checked }) => {
                                            setEnabledDays(prev => ({
                                                ...prev,
                                                [day.key]: !!checked,
                                            }));

                                            setAvailability(prev => {
                                                if (!checked) {
                                                    setPreviousAvailability(p => ({
                                                        ...p,
                                                        [day.key]: prev[day.key] || {}
                                                    }));
                                                    return {
                                                        ...prev,
                                                        [day.key]: { start: undefined, end: undefined },
                                                    };
                                                } else {
                                                    const restored = previousAvailability[day.key];
                                                    return {
                                                        ...prev,
                                                        [day.key]: restored || {},
                                                    };
                                                }
                                            });
                                        }}

                                    />
                                )}
                            </Flex>
                        ))}
                    </Flex>

                    <Flex className="buttons" gap="gap.medium" vAlign="center" hAlign="end">
                        <Button
                            content="Fechar"
                            onClick={() => dialog.url.submit('cancel')}
                        />

                        <Button
                            className={`btn-availability ${isSaving ? 'disabled' : ''} ${updatedStaffMember ? 'scheduled' : ''}`}
                            content={!updatedStaffMember ? "Salvar disponibilidade" : "Disponibilidade salva"}
                            primary
                            //disabled={!isAvailabilityChanged()}
                            icon={updatedStaffMember ? <ShiftActivityIcon outline /> : null}
                            loading={!!isSaving}
                            onClick={async () => {
                                const dayMap: Record<string, number> = {
                                    monday: 1,
                                    tuesday: 2,
                                    wednesday: 3,
                                    thursday: 4,
                                    friday: 5,
                                    saturday: 6,
                                    sunday: 0,
                                };

                                const fullDaysOfWeek = Object.keys(dayMap);

                                const workingHours = fullDaysOfWeek.map(dayKey => {
                                    const isEnabled = enabledDays[dayKey] ?? true;
                                    const times = availability[dayKey] || {};
                                    const { start, end } = times;

                                    const timeSlots = (isEnabled && start && end)
                                        ? [{
                                            startTime: {
                                                hour: parseInt(start.split(':')[0], 10),
                                                minute: parseInt(start.split(':')[1], 10),
                                                second: 0
                                            },
                                            endTime: {
                                                hour: parseInt(end.split(':')[0], 10),
                                                minute: parseInt(end.split(':')[1], 10),
                                                second: 0
                                            }
                                        }]
                                        : [];

                                    return {
                                        day: dayMap[dayKey],
                                        timeSlots
                                    };
                                });

                                const updatedStaffMember = {
                                    ...selectedStaffMember,
                                    useBusinessHours: useBusinessHours,
                                    workingHours
                                };

                                const payload = {
                                    staffMember: updatedStaffMember,
                                    bookingsId: bookingId,
                                    useBusinessHours: useBusinessHours,
                                };

                                setIsSaving(true);
                                setError(null);

                                try {
                                    await updateStaffMember(payload);
                                    setUpdatedStaffMember(true);
                                } catch (err: any) {
                                    setError('Erro ao salvar disponibilidade. Tente novamente.');
                                    console.error(err);
                                } finally {
                                    setIsSaving(false);
                                }
                            }}

                        />
                    </Flex>
                </Flex>
            )}
        </div>
    );
};

export default BookingsAvailability;
